import { BuilderMainActivity } from "@dune/types";
import { Button, ControlledInput, MultipleSelector } from "@dune/ui";
import { builderMainActivities } from "@dune/vars";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useShouldDisplayHTAmount } from "../../../../hooks/useParticipantToSubmissionForm";
import { SirenInput } from "./SirenInput";
import { ControlledPriceInput } from "../../../../components/inputs/ControlledPriceInput";
import type { BuilderToReceptionFormData } from "../../../../pages/constructionSites/reception/participants/useParticipantToReceptionForm";
import { useBuilderReceptionDocumentRows } from "../../../../pages/constructionSites/reception/participants/useParticipantToReceptionForm";
import { ReceptionDocumentsTable } from "./ReceptionDocumentsTable";
import AddressFormFields from "../../../shared/AddressFormFields";

interface BuilderToReceptionFormProps {
	constructionSiteId: number;
	onSubmit: () => void;
	onCancel: () => void;
	isSubmitting?: boolean;
}

export const BuilderToReceptionForm = ({
	constructionSiteId,
	onSubmit,
	onCancel,
	isSubmitting = false,
}: BuilderToReceptionFormProps) => {
	const {
		control,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext<BuilderToReceptionFormData>();

	const [establishmentType, setEstablishmentType] = useState<
		"french" | "foreign"
	>("french");

	const { shouldDisplayHT } = useShouldDisplayHTAmount(constructionSiteId);

	const documentRows = useBuilderReceptionDocumentRows();

	return (
		<div className="space-y-6">
			<ReceptionDocumentsTable
				constructionSiteId={constructionSiteId}
				documentRows={documentRows}
			/>

			<MultipleSelector
				label="Activités"
				options={builderMainActivities.map((activity) => ({
					value: activity,
					label: activity,
				}))}
				placeholder="Sélectionner"
				value={(watch("activities") || []).map((activity) => ({
					value: activity,
					label: activity,
				}))}
				onChange={(selectedOptions) => {
					const activities = selectedOptions.map((option) => option.value);
					setValue("activities", activities as BuilderMainActivity[]);
				}}
				error={errors.activities?.message}
			/>

			<div className="space-y-4">
				<h3 className="text-lg font-medium">Établi en France</h3>
				<div className="flex space-x-4">
					<label className="flex items-center">
						<input
							type="radio"
							name="establishmentType"
							value="french"
							checked={establishmentType === "french"}
							onChange={(e) => setEstablishmentType(e.target.value as "french")}
							className="mr-2"
						/>
						Oui
					</label>
					<label className="flex items-center">
						<input
							type="radio"
							name="establishmentType"
							value="foreign"
							checked={establishmentType === "foreign"}
							onChange={(e) =>
								setEstablishmentType(e.target.value as "foreign")
							}
							className="mr-2"
						/>
						Non
					</label>
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
				{establishmentType === "french" ? (
					<SirenInput
						name="siren"
						label="SIREN"
						placeholder="Numéro SIREN (9 chiffres)"
					/>
				) : (
					<>
						<ControlledInput
							name="registrationNumber"
							control={control}
							label="Numéro d'enregistrement"
							placeholder="Numéro d'enregistrement"
							error={errors.registrationNumber?.message}
						/>
						<ControlledInput
							name="country"
							control={control}
							label="Pays"
							placeholder="Pays"
							error={errors.country?.message}
						/>
					</>
				)}

				<ControlledInput
					name="denomination"
					control={control}
					label="Dénomination"
					placeholder="Dénomination de l'entreprise"
					error={errors.denomination?.message}
					required
				/>
			</div>

			<div className="space-y-4">
				<h3 className="text-lg font-medium">Adresse</h3>
				<AddressFormFields showBorder={true} />
			</div>

			<div className="space-y-4">
				{shouldDisplayHT ? (
					<ControlledPriceInput
						name="totalFeesHT"
						control={control}
						label="Montant du marché HT"
						placeholder="0"
						iconOrText="€ HT"
						required
					/>
				) : (
					<ControlledPriceInput
						name="totalFeesTTC"
						control={control}
						label="Montant du marché TTC"
						placeholder="0"
						iconOrText="€ TTC"
						required
					/>
				)}
			</div>

			<div className="flex justify-end space-x-3">
				<Button
					type="button"
					variant="outline"
					onClick={onCancel}
					disabled={isSubmitting}
				>
					Annuler
				</Button>
				<Button
					type="button"
					onClick={onSubmit}
					disabled={isSubmitting}
					isLoading={isSubmitting}
				>
					Enregistrer
				</Button>
			</div>
		</div>
	);
};
